import React, { useState } from 'react';
import { useExchangeRatesList, useLookupMutations, useBaseCurrency, useActiveRate } from '../db';
import { useHasPermission } from '../components/auth/RequirePermission';
import { useAuthStore } from '../stores';
import { useToast } from '../components/common/ToastProvider';
import { dayjs } from '../lib/dayjs';
import { RefreshCw, Plus, Calendar, Check, Cloud, TrendingUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { useDatabase } from '../db/DatabaseProvider';
import { performBcvSync } from '../db/sync/bcvSync';
import { Drawer } from '../components/common/Drawer';
import { DataTable, SyncStateIcon, type ColumnDef } from '../components/common/DataTable';
import { Button } from '../components/common/Button';
import type { ExchangeRateDocType } from '../db/schemas';
import { useDataTable } from '../hooks/useDataTable';

export function ExchangeRates() {
  const { rates, isLoading } = useExchangeRatesList();
  const { baseCurrency, currencySymbol } = useBaseCurrency();
  const { addExchangeRate } = useLookupMutations();
  const db = useDatabase();
  const hasPermission = useHasPermission();
  const canSyncBCV = hasPermission('rates:sync');
  const canManualOverride = hasPermission('rates:manual_override');
  const user = useAuthStore((s) => s.user);
  const toast = useToast();

  // Form State
  const [eurRate, setEurRate] = useState<number | ''>('');
  const [usdRate, setUsdRate] = useState<number | ''>('');
  const [rateUntil, setRateUntil] = useState(dayjs().add(1, 'day').format('YYYY-MM-DD'));
  const [rateSince, setRateSince] = useState(dayjs().format('YYYY-MM-DD'));
  const [bcvSinceDate, setBcvSinceDate] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSyncingBCV, setIsSyncingBCV] = useState(false);

  // Tab, Calendar & Selected Rate States
  const [activeTab, setActiveTab] = useState<'list' | 'calendar'>('list');
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);

  // Find active rates using memoized custom hook
  const { activeRate: latestRateDoc, isRateExpired } = useActiveRate();
  
  const formatDateStr = (str?: string) => {
    if (!str) return 'No disponible';
    const parts = str.substring(0, 10).split('-');
    if (parts.length !== 3) return str;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Selected rate for inspector (fallback to latest rate)
  const selectedRate = rates.find((r) => r.id === selectedRateId) || latestRateDoc;

  const handleSincronizarBCV = async () => {
    if (!db) return;
    setIsSyncingBCV(true);
    try {
      const res = await performBcvSync(db, user?.email || 'system', bcvSinceDate || undefined);
      if (res.status === 'success' || res.status === 'up_to_date' || res.addedCount > 0) {
        toast.success(res.message || 'Tasas oficiales BCV sincronizadas correctamente', 'BCV Actualizado');
        setBcvSinceDate(null);
      } else {
        toast.error(res.message || 'Error al consultar BCV', 'Fallo BCV');
      }
    } catch (err: any) {
      toast.error('Error en sincronización BCV: ' + (err.message || 'Fallo de red'), 'Fallo BCV');
    } finally {
      setIsSyncingBCV(false);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usdRate || !eurRate) {
      toast.error('Debe ingresar ambas tasas (USD y EUR)', 'Datos Incompletos');
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await addExchangeRate({
        usd: Number(usdRate),
        eur: Number(eurRate),
        since: rateSince,
        until: rateUntil,
        lastModifiedBy: 'Manual Admin',
      });
      toast.success('Tasa de cambio registrada exitosamente');
      setIsFormOpen(false);
      setUsdRate('');
      setEurRate('');
    } catch (err: any) {
      toast.error('Error al registrar tasa: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableState = useDataTable({
    data: rates,
    searchFields: [(r) => `${r.since} ${r.until} ${r.usd} ${r.eur}`],
    initialSortBy: 'since',
    initialSortOrder: 'desc',
    sortOptions: [
      { value: 'since', label: 'Vigencia' },
      { value: 'usd', label: 'Tasa USD' },
      { value: 'eur', label: 'Tasa EUR' },
    ],
    initialPageSize: '25',
    storageKey: 'depilaway-rates-page-size',
  });

  // Columns definition for Desktop Table View
  const columns: ColumnDef<ExchangeRateDocType>[] = [
    {
      key: 'since',
      header: 'Período de Vigencia',
      sortable: true,
      render: (r) => (
        <div className="text-xs font-mono text-text-primary font-bold">
          {formatDateStr(r.since)} → {formatDateStr(r.until)}
        </div>
      ),
    },
    {
      key: 'usd',
      header: 'Tasa USD (VES)',
      sortable: true,
      render: (r) => (
        <span className="font-bold font-mono text-brand-orange">
          Bs. {Number(r.usd).toFixed(4)}
        </span>
      ),
    },
    {
      key: 'eur',
      header: 'Tasa EUR (VES)',
      sortable: true,
      render: (r) => (
        <span className="font-bold font-mono text-brand-sky">
          Bs. {Number(r.eur).toFixed(4)}
        </span>
      ),
    },
    {
      key: 'until',
      header: 'Origen',
      sortable: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-surface-secondary border border-border text-xs text-text-secondary">
          <Cloud className="h-3 w-3 text-text-tertiary" />
          <span>{(r as any).source || 'BCV Oficial'}</span>
        </span>
      ),
    },
  ];

  // Custom Render for Mobile Cards (<768px)
  const renderMobileCard = (r: ExchangeRateDocType) => (
    <div className="p-4 rounded-2xl border border-border bg-surface shadow-xs space-y-3 hover:border-brand-orange/40 transition-all">
      {/* Header: Dates & Sync Icon */}
      <div className="flex items-center justify-between border-b border-border/40 pb-2">
        <div className="flex items-center gap-1.5 text-xs font-mono font-bold text-text-primary">
          <Calendar className="h-3.5 w-3.5 text-brand-orange" />
          <span>{formatDateStr(r.since)} → {formatDateStr(r.until)}</span>
        </div>
        <div className="flex items-center gap-2">
          <SyncStateIcon state={(r as any).sync_state} />
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-secondary border border-border text-[10px] text-text-tertiary">
            <Cloud className="h-3 w-3" />
            <span>{(r as any).source || 'BCV Oficial'}</span>
          </span>
        </div>
      </div>

      {/* Rates Grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="p-2.5 rounded-xl bg-brand-orange/5 border border-brand-orange/20">
          <div className="text-[10px] font-bold text-brand-orange uppercase">Tasa USD ($)</div>
          <div className="text-sm font-bold font-mono text-brand-orange mt-0.5">Bs. {Number(r.usd).toFixed(4)}</div>
        </div>
        <div className="p-2.5 rounded-xl bg-brand-sky/5 border border-brand-sky/20">
          <div className="text-[10px] font-bold text-brand-sky uppercase">Tasa EUR (€)</div>
          <div className="text-sm font-bold font-mono text-brand-sky mt-0.5">Bs. {Number(r.eur).toFixed(4)}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header & Action Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-text-primary tracking-tight">Tasas de Cambio</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Histórico y configuración de la tasa oficial del Banco Central de Venezuela (BCV)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSyncBCV && (
            <Button
              variant="secondary"
              size="md"
              icon={RefreshCw}
              isLoading={isSyncingBCV}
              loadingText="Consultando BCV..."
              onClick={handleSincronizarBCV}
            >
              Sincronizar BCV
            </Button>
          )}
          {canManualOverride && (
            <Button
              variant="primary"
              size="md"
              icon={Plus}
              onClick={() => setIsFormOpen(true)}
            >
              Tasa Manual
            </Button>
          )}
        </div>
      </div>

      {/* Current Active Rate Card */}
      {latestRateDoc && (
        <div className="p-5 rounded-2xl bg-linear-to-r from-brand-orange/10 via-surface to-brand-sky/10 border border-brand-orange/20 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-brand-orange/15 text-brand-orange">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-text-tertiary uppercase tracking-wider">Tasa Vigente Oficial</span>
                {isRateExpired ? (
                  <span className="px-2 py-0.5 rounded-full bg-error/10 text-error text-[10px] font-bold">Vencida</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-bold">Vigente</span>
                )}
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-2xl font-extrabold font-mono text-brand-orange">
                  Bs. {Number(latestRateDoc.usd).toFixed(4)} <span className="text-xs font-normal text-text-secondary">/ USD</span>
                </span>
                <span className="text-lg font-bold font-mono text-brand-sky">
                  Bs. {Number(latestRateDoc.eur).toFixed(4)} <span className="text-xs font-normal text-text-secondary">/ EUR</span>
                </span>
              </div>
              <p className="text-xs text-text-tertiary mt-1">
                Vigencia del {formatDateStr(latestRateDoc.since)} al {formatDateStr(latestRateDoc.until)} (BCV)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Unified DataTable Component */}
      <DataTable<ExchangeRateDocType>
        data={rates}
        columns={columns}
        getItemKey={(item) => item.id}
        getSyncState={(item) => (item as any).sync_state}
        tableState={tableState}
        isLoading={isLoading}
        renderMobileCard={renderMobileCard}
        searchPlaceholder="Buscar por date, origen o tasa de cambio..."
        emptyTitle="No hay tasas cambiarias registradas"
        emptyDescription="Sincroniza directamente con la API del BCV o realiza un registro manual para operar en multimoneda."
        emptyIcon={TrendingUp}
        actionButton={
          <Button variant="primary" size="sm" icon={RefreshCw} onClick={handleSincronizarBCV} isLoading={isSyncingBCV}>
            Sincronizar Tasa BCV Ahora
          </Button>
        }
      />

      {/* Drawer Manual Rate Modal */}
      <Drawer
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Registrar Tasa de Cambio Manual"
        subtitle="Ingresa el valor fijado para transactions de hoy o períodos específicos"
        icon={Plus}
        maxWidth="md"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit" form="manual-rate-form" isLoading={isSubmitting}>
              Guardar Tasa Manual
            </Button>
          </>
        }
      >
        <form id="manual-rate-form" onSubmit={handleManualSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Tasa USD (Bs.) *</label>
              <input
                required
                type="number"
                inputMode="decimal"
                step="0.0001"
                min="0.0001"
                value={usdRate}
                onChange={(e) => setUsdRate(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Ej. 36.5000"
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Tasa EUR (Bs.) *</label>
              <input
                required
                type="number"
                inputMode="decimal"
                step="0.0001"
                min="0.0001"
                value={eurRate}
                onChange={(e) => setEurRate(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Ej. 39.8000"
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Vigencia Desde *</label>
              <input
                required
                type="date"
                value={rateSince}
                onChange={(e) => setRateSince(e.target.value)}
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Vigencia Hasta *</label>
              <input
                required
                type="date"
                value={rateUntil}
                onChange={(e) => setRateUntil(e.target.value)}
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              />
            </div>
          </div>
        </form>
      </Drawer>
    </div>
  );
}
